import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  ListItem,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { SEARCH_DISEASES } from '../graphql/operations';
import { Disease } from '../types';
import { QuickDiseaseDialog } from './QuickDiseaseDialog';

// Custom debounce hook
function useDebounce<T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = React.useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  ) as T;

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

interface DiseaseSelectorProps {
  value?: Disease | null;
  onChange: (disease: Disease | null) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export const DiseaseSelector: React.FC<DiseaseSelectorProps> = ({
  value,
  onChange,
  label = 'Disease Label',
  placeholder = 'Search by disease name or ICD-10 code...',
  fullWidth = true,
  required = false,
  error = false,
  helperText,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Disease[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Use the FTS search query with manual triggering
  const { data, loading, refetch } = useQuery(SEARCH_DISEASES, {
    variables: { query: searchTerm || 'rash', limit: 10 },
    skip: true, // Don't execute automatically
  });

  // Search function
  const performSearch = useCallback(async (term: string) => {
    if (term.length >= 2) {
      const result = await refetch({ query: term, limit: 10 });
      if (result.data?.searchDiseases?.items) {
        setOptions(result.data.searchDiseases.items);
      }
    } else {
      setOptions([]);
    }
  }, [refetch]);

  // Debounced search
  const debouncedSearch = useDebounce(performSearch, 300);

  // Load all diseases when opening without search term
  useEffect(() => {
    if (open && !searchTerm) {
      refetch({ query: 'rash', limit: 50 }).then((result) => {
        if (result.data?.searchDiseases?.items) {
          setOptions(result.data.searchDiseases.items);
        }
      });
    }
  }, [open, searchTerm, refetch]);

  const handleInputChange = (event: React.SyntheticEvent, value: string) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDiseaseCreated = (newDisease: Disease) => {
    setOptions([newDisease, ...options]);
    onChange(newDisease);
    setShowCreateDialog(false);
  };

  const getOptionLabel = (option: Disease) => {
    return `${option.name}${option.icd10_code ? ` (${option.icd10_code})` : ''}`;
  };

  const renderOption = (props: any, option: Disease) => {
    return (
      <ListItem {...props} component="li">
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">{option.name}</Typography>
              {option.icd10_code && (
                <Chip
                  label={option.icd10_code}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20 }}
                />
              )}
            </Box>
          }
          secondary={option.short_description}
        />
      </ListItem>
    );
  };

  return (
    <>
    <Autocomplete
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      inputValue={searchTerm}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading}
      fullWidth={fullWidth}
      PaperComponent={({ children }) => (
        <Paper elevation={3} sx={{ maxHeight: 400 }}>
          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
            {children}
          </Box>
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button
              fullWidth
              startIcon={<Add />}
              onClick={() => {
                setShowCreateDialog(true);
                setOpen(false);
              }}
              sx={{ justifyContent: 'flex-start' }}
            >
              Add New Disease
              {searchTerm && ` "${searchTerm}"`}
            </Button>
          </Box>
        </Paper>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        searchTerm.length < 2
          ? 'Type at least 2 characters to search'
          : 'No diseases found'
      }
      sx={{
        '& .MuiAutocomplete-listbox': {
          '& .MuiListItem-root': {
            borderBottom: 1,
            borderColor: 'divider',
            '&:last-child': {
              borderBottom: 0,
            },
          },
        },
      }}
    />
    
    <QuickDiseaseDialog
      open={showCreateDialog}
      onClose={() => setShowCreateDialog(false)}
      onDiseaseCreated={handleDiseaseCreated}
      initialName={searchTerm}
    />
  </>
  );
};